/**
 * game-start-panel-etch.js — the panel-etch entrance gesture for the mode cards.
 *
 * The game-start mode cards (Skat / Ramsch) INSCRIBE as one unit instead of
 * bouncing in. The etch-and-ink vocabulary is "strokes draw on, fills bloom", and
 * a card is read top-down through it:
 *
 *   1. FRAME — an SVG rounded-rect outline overlaid on the card draws on (DrawSVG).
 *   2. SURFACE — the card's vellum fill + border BLOOM inside the finished frame at
 *      the golden cut (a fill, so it blooms — the right half of the vocabulary).
 *   3. MARKS — the chalice ICON then the LABEL inscribe onto the settled surface,
 *      each via the etch Stamp (animation/components/etch-stamp.js). An icon's
 *      shapes and a word's letter contours are MARKS, so they draw on — they do
 *      NOT ride the surface bloom up. This is the whole point of this module's
 *      rework: a card that etched its frame then *faded* its icon + label spoke two
 *      dialects in one element; now the contents inscribe in the frame's own idiom.
 *
 * A CSS border can't be DrawSVG-etched (the wall the Lothar wordmark hit — DOM
 * can't be drawn on), so the frame is a generated SVG path sized to the card's
 * measured geometry and corner radius. The easy version of the wordmark's glyph
 * extraction: a rounded rectangle is one path, no font work. The icon is already
 * an SVG (the same createChalice/createFallenChalice the logo etches); the label
 * becomes a glyph-path SVG (game-start-mode-label.js) so it too can be drawn on.
 *
 * The frame overlays the card as a SIBLING in the row, not a child: staging hides
 * the whole card via autoAlpha (opacity 0), and a child of an opacity:0 element
 * can't paint — so the frame must sit over the card, free to draw while the card
 * is hidden. At the golden cut the card's own autoAlpha brings the SURFACE up
 * (bg + border + any run badge); the icon + label are Stamp-staged hidden inside
 * it, so the bloom reveals a blank vellum face they then inscribe onto. The frame
 * then settles — to 0 in production (the edge hands back to the card's CSS
 * border), or to frameRestAlpha in the lab, which keeps the inscribed edge visible.
 *
 * Consumers: the game-start entrance (game-start-entrance.js, continuous draw)
 * and the animation-lab "Panel Etch" tab (dev/panel-etch.js, continuous + split),
 * which replays this same composition on its own demo cards so lab and prod can't
 * drift — only the lab's staging is local. `game-start-toggles-etch.js` imports
 * from here too, but for something else: it takes the stroke primitives
 * (`framePath`, `FRAME_COLOR`, `FRAME_WIDTH`) for the ROWS group's own pill
 * frames, not this gesture. Per locality / YAGNI it lives in game-start/, not a shared animation
 * component; promote on a second panel-etch site (rule of three).
 */
import { gsap } from "../vendor/gsap.js";
import { DrawSVGPlugin } from "../vendor/gsap-drawsvg.js";
import {
  ETCH_STAMP_DEFAULTS,
  prepareStamp,
  stageStamp,
  addStamp,
  shedStampResidue,
} from "../animation/components/etch-stamp.js";

gsap.registerPlugin(DrawSVGPlugin);

const SVG_NS = "http://www.w3.org/2000/svg";
export const FRAME_COLOR = "#F1F5F9"; // vellum — the inscribed edge, matching the icon etch
export const FRAME_WIDTH = 1.6; // play-test: the inscribed edge's stroke width (px)
const FRAME_CLASS = "game-start-panel-frame"; // ephemeral animation DOM, shed at the end

// The chalice icon inscribes through the etch Stamp in CLONE mode (a fill icon, the
// same factory the logo etches at PIP scale) — etch-only at rest size, its strokes
// in the icon's own `currentColor` (slate, or amber when the card is selected).
const ICON_ETCH_CONFIG = {
  ...ETCH_STAMP_DEFAULTS,
  stampScale: 1, // etch at rest size — the icon inscribes, it doesn't announce-press
  stampPress: false, // etch-only: end at the ink bloom, no press
  etchS: 0.26, // play-test: each chalice stroke's draw-on span — legible at 46px
  etchStaggerS: 0.03, // play-test: rhythm between the chalice's own strokes
  inkS: 0.24, // play-test: the fill bloom over the drawn contours
  etchWidth: 1.4, // why: viewBox units (~24-unit icon box) — reads at rest size
};
// The label inscribes through the SAME Stamp, clone mode (filled glyph paths) —
// but its glyphs live in a 1000-unit font em, so the stroke width is in font units
// and the per-letter rhythm is the wordmark's, not the icon's. Mirrors the
// game-start logo wordmark / Play label configs.
const LABEL_ETCH_CONFIG = {
  ...ETCH_STAMP_DEFAULTS,
  stampScale: 1, // etch at rest size — the word inscribes, never presses
  stampPress: false, // etch-only
  etchS: 0.26, // play-test: each letter's outline draw-on span
  etchStaggerS: 0.05, // play-test: gap between letters — the inscription rhythm
  inkS: 0.26, // play-test: the fill bloom
  etchWidth: 11, // why: font units (1000upm em) — a hairline trace of the contour
};

// The production baseline, tunable live in the animation-lab "Panel Etch" tab.
// frameRestAlpha is intentionally absent: production sheds the frame to 0 (the
// edge hands back to the card's CSS border); the lab supplies its own rest alpha.
export const PANEL_ETCH_DEFAULTS = {
  frameDrawS: 0.7, // play-test: one panel's frame draw-on span
  cardStaggerS: 0.12, // play-test: gap between the two panels' inscriptions (Skat, then Ramsch)
  bloomAtFrac: 0.618, // why: golden cut — the surface blooms while the frame is still finishing over it
  bloomS: 0.5, // play-test: the surface (bg + border) bloom span
  contentAtFrac: 0.72, // play-test: where in the frame draw the icon starts inscribing — shortly after the surface bloom begins under it (at defaults the marks overlap the still-blooming surface, not a finished one)
  labelLeadS: 0.14, // play-test: gap from the icon's inscription to the label's — top-to-bottom, the card writing itself out
  frameWidth: FRAME_WIDTH, // the panel SVG is 1:1 with px, so the shared width applies unscaled
};

/**
 * Compose the panel-etch entrance over the .mode-card children of `container`
 * onto a fresh timeline: each card inscribes its frame, blooms its surface at the
 * golden cut, then inscribes its icon + label onto that surface — staggered
 * Skat → Ramsch. Returns { tl, residue }: the caller weaves `tl` onto its master
 * and sheds `residue` at the end (production) or keeps the settled frame visible
 * (the lab).
 *
 * Measures each card live: the card must be laid out and is staged via autoAlpha
 * (which preserves the layout box) — never display:none (zero box → degenerate
 * path). No reduced-motion gate here on purpose — the gate belongs to the caller
 * (playGameStartEntrance) so the lab can always replay the composition.
 *
 * @param {HTMLElement} container the .mode-cards row
 * @param {"continuous"|"split"} drawStyle continuous (entrance) or split (lab only)
 * @param {object} c merged tuning (PANEL_ETCH_DEFAULTS + overrides; frameRestAlpha optional)
 */
export function addPanelEtch(container, drawStyle, c) {
  // Anchor the absolute frame overlays to the row (flex layout is unaffected).
  container.style.position = "relative";
  const tl = gsap.timeline();
  const cards = [...container.querySelectorAll(":scope > .mode-card")];
  const frames = [];
  const contents = [];
  cards.forEach((card, i) => {
    gsap.set(card, { autoAlpha: 0 }); // stage hidden — the bloom brings the surface up
    const { svg, path } = buildFrameOverlay(container, card, drawStyle, c);
    frames.push(svg);
    const content = stageCardContent(card); // build + stage the icon/label etch Stamps
    contents.push(content);
    addCardEtch(tl, card, path, content, drawStyle, c, i * c.cardStaggerS);
  });
  return { tl, residue: { container, cards, frames, contents } };
}

// Prepare the card's two marks — the chalice icon (.mode-icon) and the label
// (.mode-label) — for the etch and stage them HIDDEN, so the surface bloom reveals
// a blank face they then inscribe onto. The host is the real .mode-* div (etch-only
// at scale 1 means the Stamp never transforms it, so no throwaway wrapper is needed,
// unlike the logo's host-wrapped pips). A label that fell back to plain text has no
// `> svg` for the Stamp — it stays null and simply blooms with the surface.
function stageCardContent(card) {
  return {
    icon: prepareContentStamp(
      card.querySelector(":scope > .mode-icon"),
      ICON_ETCH_CONFIG,
    ),
    label: prepareContentStamp(
      card.querySelector(":scope > .mode-label"),
      LABEL_ETCH_CONFIG,
    ),
  };
}

// `host` is a mode card's .mode-icon / .mode-label — both always present
// (createModeCard builds them), so it's trusted non-null. The Stamp returns null
// only when the host has no `> svg` (a text-fallback label); that mark stays null
// and blooms with the surface instead of inscribing.
function prepareContentStamp(host, config) {
  const stamp = prepareStamp(host, config);
  if (stamp == null) return null;
  stageStamp(host, stamp, config, { opacity: 1 });
  return { host, stamp };
}

// Weave one card's frame inscribe → surface bloom → icon + label inscribe onto
// `tl` at `at`.
function addCardEtch(tl, card, path, content, drawStyle, c, at) {
  const from = drawStyle === "split" ? "50% 50%" : "0% 0%";
  gsap.set(path, { drawSVG: from, opacity: 1 });
  tl.to(
    path,
    { drawSVG: "0% 100%", duration: c.frameDrawS, ease: "power1.inOut" },
    at,
  );
  // The surface blooms at the golden cut, strokes still finishing over it: bg +
  // border (+ any run badge) fade up. The icon + label are Stamp-staged hidden, so
  // they don't ride this — they inscribe next, onto the now-visible face.
  const bloomAt = at + c.frameDrawS * c.bloomAtFrac;
  tl.to(
    card,
    { autoAlpha: 1, duration: c.bloomS, ease: "power2.out" },
    bloomAt,
  );
  // The marks inscribe top-to-bottom — the chalice first, the label trailing it by
  // labelLeadS, so the card reads as writing itself out rather than two pop-ins.
  const iconAt = at + c.frameDrawS * c.contentAtFrac;
  addContentEtch(tl, content.icon, ICON_ETCH_CONFIG, iconAt);
  addContentEtch(tl, content.label, LABEL_ETCH_CONFIG, iconAt + c.labelLeadS);
  // The hot inscription settles once the fill has taken: to 0 in production (the
  // edge hands back to the card's CSS border), or frameRestAlpha in the lab.
  tl.to(
    path,
    { opacity: c.frameRestAlpha ?? 0, duration: c.bloomS, ease: "power2.out" },
    at + c.frameDrawS,
  );
}

// Add one mark's etch Stamp at `at` on its own sub-timeline (so the Stamp's
// internal stroke positions are relative to the mark, not the card timeline). A
// null mark (text-fallback label) is a no-op — it blooms with the surface instead.
function addContentEtch(tl, mark, config, at) {
  if (mark == null) return;
  const sub = gsap.timeline();
  addStamp(sub, mark.host, mark.stamp, config, { opacity: 1 });
  tl.add(sub, at);
}

// Build an SVG frame overlay sized to the card's measured box, position it over
// the card in `container` (sibling overlay — see the module header), append it,
// and return { svg, path } with the path drawable (DrawSVG).
function buildFrameOverlay(container, card, drawStyle, c) {
  const w = card.offsetWidth;
  const h = card.offsetHeight;
  const r = parseFloat(getComputedStyle(card).borderTopLeftRadius) || 0;
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.classList.add(FRAME_CLASS);
  svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
  svg.style.cssText =
    `position:absolute; left:${card.offsetLeft}px; top:${card.offsetTop}px; ` +
    `width:${w}px; height:${h}px; overflow:visible; pointer-events:none;`;
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", framePath(w, h, r, c.frameWidth / 2, drawStyle));
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", FRAME_COLOR);
  path.setAttribute("stroke-width", c.frameWidth);
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  svg.appendChild(path);
  container.appendChild(svg);
  return { svg, path };
}

/**
 * Shed one entrance's panel residue: remove the frame overlays, return each card's
 * icon + label to their factory SVG (the Stamp's unwrap), and clear the GSAP
 * inline residue (the card's autoAlpha), so the rested cards equal the factory
 * render. Reset the row's anchoring position. A SEPARATE shed from the logo's
 * host-unwrap — panel residue is overlay-removal + per-mark Stamp shed + clearProps,
 * not a wrapped host (unwrapEtchHost would mis-handle a .mode-card).
 */
export function shedPanelEtch({ container, cards, frames, contents }) {
  for (const svg of frames) svg.remove();
  for (const content of contents) shedCardContent(content);
  gsap.set(cards, { clearProps: "all" });
  container.style.position = "";
}

function shedCardContent({ icon, label }) {
  if (icon != null) shedStampResidue(icon.host, icon.stamp);
  if (label != null) shedStampResidue(label.host, label.stamp);
}

// A clockwise rounded-rect outline inset by `inset` (half the stroke, so the
// stroke sits on the card edge). CONTINUOUS starts at the top-left corner; SPLIT
// starts at the bottom-edge center so the path's midpoint lands at the top-edge
// center — DrawSVG growing from "50% 50%" then inscribes from the top down both
// sides, meeting at the bottom.
export function framePath(w, h, r, inset, drawStyle) {
  const x0 = inset;
  const y0 = inset;
  const x1 = w - inset;
  const y1 = h - inset;
  const rr = Math.max(0, r - inset);
  const arc = (x, y) => `A ${rr} ${rr} 0 0 1 ${x} ${y}`;
  // Each edge traced to the next corner, then that corner's arc — clockwise.
  const top = `L ${x1 - rr} ${y0} ${arc(x1, y0 + rr)}`;
  const right = `L ${x1} ${y1 - rr} ${arc(x1 - rr, y1)}`;
  const bottom = `L ${x0 + rr} ${y1} ${arc(x0, y1 - rr)}`;
  const left = `L ${x0} ${y0 + rr} ${arc(x0 + rr, y0)}`;
  // SPLIT starts at the bottom-edge center so DrawSVG's "50% 50%" midpoint lands
  // at the top center, inscribing down both sides to meet at the bottom.
  if (drawStyle === "split") {
    return `M ${w / 2} ${y1} ${bottom} ${left} ${top} ${right} L ${w / 2} ${y1} Z`;
  }
  // CONTINUOUS starts at the top-left corner and draws clockwise around.
  return `M ${x0 + rr} ${y0} ${top} ${right} ${bottom} ${left} Z`;
}
